import React, {
    createContext,
    useCallback,
    useContext,
    useState,
    useEffect,
    useRef
} from 'react';

import { cn, decodeHtmlEntities } from "@/lib/utils"


// Types for menu structure
interface MenuItem {
    id: string;
    label: string;
    href?: string;
    children?: MenuItem[];
}

interface MenuContextType {
    menuItems: MenuItem[];
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    isRefetching: boolean;
}


const createMenuItem = (data: any): MenuItem =>{
    return {
        id: String(data.id),
        // WordPress stores titles HTML-encoded: an ampersand comes back as
        // "&#038;", an apostrophe as "&#039;". React renders strings as text,
        // so without decoding the entity itself is what the visitor reads.
        label: decodeHtmlEntities(String(data.title)),
        href: data.resolved_url,
        children: Array.isArray(data.children)
            ? data.children.map(createMenuItem)
            : undefined
    };
}

// Menu data is kept outside the component tree because the tree is torn down
// far more often than a menu changes: Header and Footer are rendered inside
// each page component, and App.tsx keys <Routes> by pathname, so every
// navigation unmounts and remounts every provider. Held only in component
// state, each of those remounts refetched the menu and — because the provider
// starts out loading — repainted the nav as a skeleton first, so clicking a
// menu item made the whole header jump.
let menuCache = new Map<string, MenuItem[]>();
// In-flight reads, so providers sharing a menu id (a desktop nav and a mobile
// drawer both wrapping menu 16) issue one request between them.
let inFlightRequests = new Map<string, Promise<MenuItem[]>>();

const loadMenuItems = (menuId: string): Promise<MenuItem[]> => {
    const cached = menuCache.get(menuId);
    if (cached) return Promise.resolve(cached);

    const pending = inFlightRequests.get(menuId);
    if (pending) return pending;

    const cacheAtRequest = menuCache;
    const request = wvcClient
        .getMenuItems({ menuId: parseInt(menuId) })
        .then((data: any[]) => {
            const items: MenuItem[] = data.map(createMenuItem);
            // A menu edit while this was in flight replaced the cache, so this
            // is the pre-edit result and must not be written back over it.
            if (cacheAtRequest === menuCache) menuCache.set(menuId, items);
            return items;
        })
        .finally(() => {
            if (inFlightRequests.get(menuId) === request) inFlightRequests.delete(menuId);
        });

    inFlightRequests.set(menuId, request);
    return request;
};

// One listener for the module rather than one per provider: the event only says
// "a menu changed", so handling it per provider refetched the same menu once
// for every provider mounted.
const menuRefreshSubscribers = new Set<() => void>();

const invalidateMenus = () => {
    menuCache = new Map();
    inFlightRequests = new Map();
    menuRefreshSubscribers.forEach((refetch) => refetch());
};

if (typeof window !== 'undefined') {
    window.addEventListener("WVC_MENU_REFRESH", invalidateMenus);
}

// Create context
const MenuContext = createContext<MenuContextType | null>(null);

// Custom hook to use menu context
const useMenu = () => {
    const context = useContext(MenuContext);
    if (!context) {
        throw new Error('useMenu must be used within a MenuProvider');
    }
    return context;
};

// MenuProvider component
type WordpressMenuProviderProps = {
    children: React.ReactNode;
    menu_id: string;
} & React.HTMLAttributes<HTMLDivElement>;

const WordpressMenuProvider = ({children, menu_id, ...divProps}: WordpressMenuProviderProps) => {
    // Seeded from the cache so a remount renders the menu it already has. This
    // is what keeps a navigation from flashing the skeleton and reflowing the
    // header; a first visit still starts empty and loading.
    const [menuItems, setMenuItems] = useState<MenuItem[]>(() => menuCache.get(menu_id) ?? []);
    const [loading, setLoading] = useState(() => !menuCache.has(menu_id));
    const [error, setError] = useState<string | null>(null);
    const [isRefetching, setIsRefetching] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const handleError = useCallback(() => {
        const message = 'Failed to fetch menu items';
        setError(message);
    }, []);

    const fetchMenuItems = useCallback(async (signal?: AbortSignal) => {
        // Only show the skeleton when there is nothing to show instead.
        if (!menuCache.has(menu_id)) setLoading(true);
        setIsRefetching(true);
        setError(null);

        try {
            const items = await loadMenuItems(menu_id);
            // getMenuItems reaches the editor over postMessage and the theme
            // through a shared client, so the request cannot be aborted. The
            // signal is what drops a result whose provider has unmounted.
            if (signal?.aborted) return;
            // A menu edit that landed while this was in flight has already
            // cached the fresh menu, which supersedes this result.
            setMenuItems(menuCache.get(menu_id) ?? items);
        } catch {
            if (signal?.aborted) return;
            handleError();
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
                setIsRefetching(false);
            }
        }
    }, [menu_id, handleError]);

    useEffect(() => {
        // Create new AbortController for this request
        abortControllerRef.current = new AbortController();

        fetchMenuItems(abortControllerRef.current.signal);

        // invalidateMenus has already emptied the cache above, so this reads
        // again. Whether that reaches WordPress is the editor's call — it
        // refreshes what it caches when a menu is edited.
        const onMenusInvalidated = () => {
            fetchMenuItems(abortControllerRef.current?.signal);
        };
        menuRefreshSubscribers.add(onMenusInvalidated);

        // Cleanup function
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            menuRefreshSubscribers.delete(onMenusInvalidated);
        };
    }, [fetchMenuItems]);

    const refetch = async () => {
        // Create new AbortController for refetch
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        // An explicit refetch means the cached copy is suspect.
        menuCache.delete(menu_id);
        inFlightRequests.delete(menu_id);
        await fetchMenuItems(abortControllerRef.current.signal);
    };

    const value: MenuContextType = {
        menuItems,
        loading,
        error,
        refetch,
        isRefetching
    };

    return (
        <MenuContext.Provider value={value}>
            <div
                {...divProps}
                data-wvc-dynamic="MenuProvider"
                data-wvc-menu_id={menu_id}
            >
                {children}
            </div>
        </MenuContext.Provider>
    );
};

export const MenuProvider = WordpressMenuProvider;
export { WordpressMenuProvider, MenuItem, useMenu };
